import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import { useToast } from '../components/ui/Toast'
import { Button } from '../components/ui/Button'
import { GlowCard } from '../components/ui/GlowCard'
import { StatCard } from '../components/ui/StatCard'
import { EmptyState } from '../components/ui/EmptyState'

interface VultrInstance {
  id: string
  vultr_id: string
  label: string | null
  hostname: string | null
  status: string | null
  power_status: string | null
  region: string | null
  region_name: string | null
  plan: string | null
  vcpu_count: number | null
  ram_mb: number | null
  disk_gb: number | null
  os: string | null
  main_ip: string | null
  v6_main_ip: string | null
  internal_ip: string | null
  bandwidth_gb_allowed: number | null
  bandwidth_gb_used: number | null
  cost_per_month: number | null
  staging_status: string
  client_id: string | null
  client_name?: string
  sell_price: number | null
  last_sync: string | null
}

interface CostSummary {
  total_cost: number
  total_revenue: number
  total_margin: number
  instance_count: number
  by_client: { client_id: string; client_name: string; cost: number; revenue: number; count: number }[]
}

function ServerIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
}

function DollarIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
}

function ActivityIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
}

function CloudIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
}

export default function CloudInfrastructure() {
  const [instances, setInstances] = useState<VultrInstance[]>([])
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const { toast } = useToast()

  const load = useCallback(async () => {
    try {
      const [instResp, costResp] = await Promise.all([
        api.get<{ data: VultrInstance[] }>('/integrations/vultr/instances?staging_status=assigned'),
        api.get<CostSummary>('/integrations/vultr/cost-summary'),
      ])
      setInstances(instResp.data || [])
      setCostSummary(costResp)
    } catch { /* ignore */ }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const syncAll = async () => {
    setSyncing(true)
    try {
      const conns = await api.get<{ data: { id: string }[] }>('/integrations/vultr/connections')
      for (const conn of (conns.data || [])) {
        await api.post(`/integrations/vultr/connections/${conn.id}/sync`)
      }
      toast('Sync complete', 'success')
      load()
    } catch {
      toast('Sync failed', 'error')
    }
    setSyncing(false)
  }

  const instanceAction = async (id: string, action: 'reboot' | 'halt' | 'start') => {
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this instance?`)) return
    try {
      await api.post(`/integrations/vultr/instances/${id}/${action}`)
      toast(`${action} command sent`, 'success')
      load()
    } catch {
      toast(`${action} failed`, 'error')
    }
  }

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--text-3)' }}>Loading cloud infrastructure...</div>
  }

  if (instances.length === 0 && !costSummary?.instance_count) {
    return (
      <div style={{ padding: 24 }}>
        <EmptyState
          icon={CloudIcon}
          title="No cloud instances"
          description="Connect your Vultr account in Settings → Integrations → Vultr to manage cloud VPS instances."
        />
      </div>
    )
  }

  const running = instances.filter(i => i.power_status === 'running').length
  const stopped = instances.length - running

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)', margin: 0 }}>Cloud Infrastructure</h1>
        <Button onClick={syncAll} loading={syncing} variant="secondary">Sync Now</Button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16 }}>
        <StatCard icon={ServerIcon} label="Total Instances" value={instances.length} delay={0} />
        <StatCard icon={ActivityIcon} label="Running" value={running} delay={100} />
        <StatCard icon={ServerIcon} label="Stopped" value={stopped} delay={200} />
        <StatCard icon={DollarIcon} label="Monthly Cost" value={costSummary?.total_cost ?? 0} prefix="$" delay={300} />
        {(costSummary?.total_revenue ?? 0) > 0 && (
          <StatCard icon={DollarIcon} label="Monthly Revenue" value={costSummary?.total_revenue ?? 0} prefix="$" delay={400} />
        )}
      </div>

      {/* Client Cost Breakdown */}
      {costSummary && costSummary.by_client.length > 0 && (
        <GlowCard style={{ padding: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 12 }}>Cost by Client</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>VMs</th>
                <th style={thStyle}>Your Cost</th>
                <th style={thStyle}>You Charge</th>
                <th style={thStyle}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {costSummary.by_client.map(c => {
                const margin = c.revenue - c.cost
                const pct = c.cost > 0 ? Math.round((margin / c.cost) * 100) : 0
                return (
                  <tr key={c.client_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}><span style={{ fontWeight: 500 }}>{c.client_name}</span></td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--mono)' }}>{c.count}</td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--mono)' }}>${c.cost.toFixed(0)}/mo</td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--mono)', color: 'var(--accent)' }}>${c.revenue.toFixed(0)}/mo</td>
                    <td style={tdStyle}>
                      <span style={{
                        fontFamily: 'var(--mono)', fontWeight: 600,
                        color: margin >= 0 ? 'var(--ok)' : 'var(--err)',
                      }}>
                        ${margin.toFixed(0)} ({pct}%)
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </GlowCard>
      )}

      {/* Instance Table */}
      <div style={{
        background: 'var(--bg-1)', borderRadius: 'var(--r-l)',
        border: '1px solid var(--border)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          fontSize: 14, fontWeight: 700, color: 'var(--text-1)',
        }}>
          Instances
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Region</th>
              <th style={thStyle}>IP</th>
              <th style={thStyle}>Specs</th>
              <th style={thStyle}>Cost</th>
              <th style={thStyle}>Client</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {instances.map(inst => (
              <tr key={inst.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={tdStyle}>
                  <span
                    style={{ fontWeight: 500, color: 'var(--accent)', cursor: 'pointer' }}
                    onClick={() => setDetailId(detailId === inst.id ? null : inst.id)}
                  >
                    {inst.label || inst.hostname || 'Unnamed'}
                  </span>
                  {inst.os && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{inst.os}</div>}
                </td>
                <td style={tdStyle}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                  }}>
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%',
                      background: inst.power_status === 'running' ? 'var(--ok)' : 'var(--err)',
                    }} />
                    {inst.power_status === 'running' ? 'Running' : 'Stopped'}
                  </span>
                </td>
                <td style={tdStyle}>
                  <span style={{ fontSize: 12 }}>{inst.region_name || inst.region || '—'}</span>
                </td>
                <td style={{ ...tdStyle, fontFamily: 'var(--mono)', fontSize: 12 }}>{inst.main_ip || '—'}</td>
                <td style={{ ...tdStyle, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text-3)' }}>
                  {inst.vcpu_count}vCPU / {inst.ram_mb ? Math.round(inst.ram_mb / 1024) : '?'}GB / {inst.disk_gb}GB
                </td>
                <td style={{ ...tdStyle, fontFamily: 'var(--mono)' }}>
                  ${inst.cost_per_month?.toFixed(0) ?? '?'}/mo
                </td>
                <td style={tdStyle}>{inst.client_name || '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <Button size="sm" variant="ghost" onClick={() => instanceAction(inst.id, 'reboot')}>Reboot</Button>
                    {inst.power_status === 'running' ? (
                      <Button size="sm" variant="ghost" onClick={() => instanceAction(inst.id, 'halt')}>Stop</Button>
                    ) : (
                      <Button size="sm" variant="secondary" onClick={() => instanceAction(inst.id, 'start')}>Start</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Inline Detail Panel */}
      {detailId && <InstanceDetail instanceId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  )
}

function InstanceDetail({ instanceId, onClose }: { instanceId: string; onClose: () => void }) {
  const [instance, setInstance] = useState<VultrInstance | null>(null)
  const [bandwidth, setBandwidth] = useState<{ date: string; incoming_bytes: number; outgoing_bytes: number }[]>([])

  useEffect(() => {
    Promise.all([
      api.get<VultrInstance>(`/integrations/vultr/instances/${instanceId}`),
      api.get<{ data: { date: string; incoming_bytes: number; outgoing_bytes: number }[] }>(`/integrations/vultr/instances/${instanceId}/bandwidth`),
    ]).then(([inst, bw]) => {
      setInstance(inst)
      setBandwidth(bw.data || [])
    }).catch(() => {})
  }, [instanceId])

  if (!instance) return null

  return (
    <GlowCard style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }}>{instance.label || 'Instance'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--mono)', marginTop: 4 }}>
            Vultr ID: {instance.vultr_id}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
        <InfoItem label="Status" value={instance.power_status || '—'} />
        <InfoItem label="OS" value={instance.os || '—'} />
        <InfoItem label="Plan" value={instance.plan || '—'} mono />
        <InfoItem label="vCPU" value={String(instance.vcpu_count || 0)} />
        <InfoItem label="RAM" value={`${instance.ram_mb ? Math.round(instance.ram_mb / 1024) : '?'} GB`} />
        <InfoItem label="Disk" value={`${instance.disk_gb || '?'} GB`} />
        <InfoItem label="IP" value={instance.main_ip || '—'} mono />
        <InfoItem label="Internal IP" value={instance.internal_ip || '—'} mono />
        <InfoItem label="Region" value={instance.region_name || instance.region || '—'} />
        <InfoItem label="Bandwidth" value={`${instance.bandwidth_gb_allowed || '?'} GB`} />
        <InfoItem label="Cost" value={`$${instance.cost_per_month?.toFixed(2) ?? '?'}/mo`} mono />
        <InfoItem label="Created" value={instance.last_sync ? new Date(instance.last_sync).toLocaleDateString() : '—'} />
      </div>

      {bandwidth.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8 }}>Bandwidth (Last 30 Days)</div>
          <div style={{ display: 'flex', gap: 4, height: 60, alignItems: 'flex-end' }}>
            {bandwidth.slice(-30).map((b, i) => {
              const total = b.incoming_bytes + b.outgoing_bytes
              const maxBytes = Math.max(...bandwidth.map(x => x.incoming_bytes + x.outgoing_bytes), 1)
              const height = Math.max(2, (total / maxBytes) * 56)
              return (
                <div
                  key={i}
                  title={`${b.date}: ${formatBytes(total)}`}
                  style={{
                    flex: 1, height, borderRadius: 2,
                    background: 'var(--accent)', opacity: 0.6,
                  }}
                />
              )
            })}
          </div>
        </div>
      )}
    </GlowCard>
  )
}

function InfoItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ background: 'var(--bg-2)', borderRadius: 'var(--r-m)', padding: '8px 12px' }}>
      <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 2, fontWeight: 500, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', fontFamily: mono ? 'var(--mono)' : 'var(--font)' }}>{value}</div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 16px', fontSize: 11,
  fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
}
